import { Timestamp } from '@angular/fire/firestore';
import { buildSegmentedSchedule, EngineEvent, EngineLoanType } from '../utils/loan-schedule.engine';

export enum PaymentPeriod {
    Monthly = 'Monthly',
    Quarterly = 'Quarterly',
    Yearly = 'Yearly'
}

/**
 * Unified definition of loan types supported by the application
 * 
 * @description
 * - Fixed: Standard mortgage loan with fixed monthly payments where the ratio of principal to interest
 *   changes over time (more interest early on, more principal later). May have fixed or variable interest rates.
 * - FixedCapital: Fixed capital amortization loan where a fixed amount of principal is paid each month
 *   (e.g., 1/120th for a 10-year loan) plus interest on the remaining balance. May have fixed or variable interest.
 * - InterestOnly: Interest-only loan where only interest is paid during the loan term,
 *   with the full principal due at maturity (also known as a Bullet loan). May have fixed or variable interest.
 */
export enum LoanType {
    Fixed = 'fixed',                  // Fixed payment mortgage loan
    FixedCapital = 'fixedcapital',    // Fixed capital amortization
    InterestOnly = 'interestonly'     // Interest-only/bullet loan
}

/**
 * Lifecycle status of a loan. `active` is the default; a loan becomes `closed`
 * when fully repaid / the property is sold, and `refinanced` when it has been
 * replaced by a new loan (the new loan carries `refinancedFromLoanUID`).
 */
export enum LoanStatus {
    Active = 'active',
    Closed = 'closed',
    Refinanced = 'refinanced'
}

/**
 * The "avenants" (amendments) applied to a loan during its life. Each event has
 * a type + an effective date + type-specific parameters; the amortization engine
 * walks them in date order to build a segmented schedule (see
 * `shared/utils/loan-schedule.engine.ts`). A loan with no events behaves exactly
 * as before (the legacy single-segment schedule).
 */
export enum LoanEventType {
    EarlyRepayment = 'early_repayment',       // partial prepayment against capital
    Closure = 'closure',                      // total payoff / sold / refinanced-out
    CapitalSuspension = 'capital_suspension', // payment holiday (date → date)
    RateChange = 'rate_change',               // revision / renegotiation / convert
    TermChange = 'term_change'                // extend / shorten the term
}

/** Partial early repayment: keep the term (lower payment) or keep the payment (shorter term). */
export enum EarlyRepaymentMode {
    ReducePayment = 'reduce_payment',
    ReduceTerm = 'reduce_term'
}

/** Capital-suspension scope: pay interest only, or defer everything (interest capitalized). */
export enum SuspensionScope {
    CapitalOnly = 'capital_only',
    Full = 'full'
}

/** What happens at the end of a suspension: push the maturity out, or catch up (higher payments). */
export enum SuspensionAfter {
    Extend = 'extend',
    CatchUp = 'catch_up'
}

/** Rate change: keep the term (recompute payment) or keep the payment (term floats). */
export enum RateChangeRecalc {
    Payment = 'payment',
    Term = 'term'
}

/** Why a loan was closed — drives the UI label + status mapping. */
export enum ClosureReason {
    PaidOff = 'paid_off',
    Sold = 'sold',
    Refinanced = 'refinanced'
}

/**
 * A single amendment to a loan. Optional fields are populated per `type`; only
 * the populated ones are persisted (frontend Firestore has no
 * `ignoreUndefinedProperties`, so writers MUST strip undefined — see
 * `LoansService`).
 */
export interface LoanEvent {
    id: string;
    type: LoanEventType;
    effectiveDate: Timestamp;
    // early_repayment
    amount?: number;
    repaymentMode?: EarlyRepaymentMode;
    // capital_suspension
    suspensionEndDate?: Timestamp;
    suspensionScope?: SuspensionScope;
    suspensionAfter?: SuspensionAfter;
    // rate_change (stored as a DECIMAL rate, e.g. 0.024 — same convention as interest.rate)
    newRate?: number;
    rateRecalc?: RateChangeRecalc;
    // term_change (new TOTAL term in months, counted from the loan start date)
    newNumberOfMonths?: number;
    // closure
    closureReason?: ClosureReason;
    refinancedToLoanUID?: string;
    // costs (Belgian context) + meta
    indemnity?: number;   // indemnité de remploi
    fees?: number;
    note?: string;
    documentUID?: string;
    createdAt?: Timestamp;
}

/**
 * One unit's derived share of a building-level loan.
 *
 * A "building loan" is a SINGLE `Loan` attached to a building (via
 * `Loan.buildingGroupUID`) carrying the full principal. The per-unit view is
 * NOT a separate loan — it is a projection computed at read time from these
 * allocations (see `shared/utils/loan-allocation.ts`). `share` is a fraction in
 * [0,1]; across a loan's allocations the shares sum to EXACTLY 1 (the last unit
 * absorbs the rounding remainder) so the projected parts recompose the whole.
 */
export interface LoanAllocation {
    propertyUID: string;
    share: number;          // fraction (0..1); sums to exactly 1 across the loan
    purchasePrice: number;  // snapshot of the unit price used to derive the share (display/audit)
}

export class Loan {
    loanUID: string;
    propertyUID: string;
    ownerUID: string;
    /**
     * When set, this is a BUILDING loan: it belongs to the building
     * (PropertyGroup) `buildingGroupUID` as a whole, `propertyUID` is empty,
     * and `allocations` holds the per-unit pro-rata split. Absent for ordinary
     * single-property loans. Kept optional + only-assigned-when-present so a
     * legacy/standalone loan never serialises an `undefined` (frontend
     * Firestore has no ignoreUndefinedProperties).
     */
    buildingGroupUID?: string;
    allocations?: LoanAllocation[];
    startDate: Timestamp;
    numberOfMonths: number;
    simulation: boolean;
    /**
     * Lifecycle amendments (remboursement anticipé, suspension, révision de taux,
     * etc.). When non-empty the amortization table is built by the segmented
     * engine; when empty the loan behaves exactly as before. Always an array so
     * no `undefined` is ever serialised.
     */
    loanEvents: LoanEvent[];
    /** Lifecycle status; defaults to `active`. */
    status: LoanStatus;
    /** Set on the NEW loan created by a refinancing — points back to the old loan. */
    refinancedFromLoanUID?: string;
    loanProvider?: {
        name: string;
        lenderUID?: string;
    };
    loanType?: string;
    loanName?: string;

    interest: {
        rate: number;
        variable: boolean;
        interestPaymentPeriod: PaymentPeriod;
    };

    creditCost: {
        openingFees: number;
        recurringFees: number;
        recurringFeePeriod: number;
        mortageFee: number;
    };

    capital: {
        nominalAmount: number;
        repayment: boolean;
        fixedInstallment: boolean;
        capitalPaymentPeriod: number;
    };

    // Only store the loan amortization table
    loanTable: LoanTableEntry[];

    // Static calculator reference to be injected by the service module.
    // The concrete service is in the feature layer (LoanCalculator);
    // this domain file declares only the subset of methods it invokes
    // to avoid a circular import.
    private static _calculator: LoanCalculatorContract | null = null;

    public static set calculator(calculatorService: LoanCalculatorContract) {
        Loan._calculator = calculatorService;
    }

    private get calculator(): LoanCalculatorContract | null {
        return Loan._calculator;
    }

    constructor(init?: Partial<Loan>) {
        this.loanUID = init?.loanUID ?? '';
        this.propertyUID = init?.propertyUID ?? '';
        this.ownerUID = init?.ownerUID ?? '';
        this.startDate = init?.startDate instanceof Timestamp 
            ? init.startDate 
            : Timestamp.fromDate(init?.startDate ?? new Date());
        this.numberOfMonths = init?.numberOfMonths ?? 12;
        this.simulation = init?.simulation ?? false;
        this.loanType = init?.loanType ?? LoanType.Fixed;
        
        // Explicitly initialize loanProvider
        this.loanProvider = init?.loanProvider ?? {
            name: '',
            lenderUID: ''
        };

        this.interest = {
            rate: init?.interest?.rate ?? 0.01,
            variable: init?.interest?.variable ?? false,
            interestPaymentPeriod: init?.interest?.interestPaymentPeriod ?? PaymentPeriod.Monthly
        };

        this.capital = {
            nominalAmount: init?.capital?.nominalAmount ?? 100000,
            repayment: init?.capital?.repayment ?? true,
            fixedInstallment: init?.capital?.fixedInstallment ?? false,
            capitalPaymentPeriod: init?.capital?.capitalPaymentPeriod ?? 1
        };

        this.creditCost = {
            openingFees: init?.creditCost?.openingFees ?? 0,
            recurringFees: init?.creditCost?.recurringFees ?? 0,
            recurringFeePeriod: init?.creditCost?.recurringFeePeriod ?? 0,
            mortageFee: init?.creditCost?.mortageFee ?? 0
        };

        // Building-loan fields: only assign when present so an ordinary loan
        // never carries an `undefined` property into a frontend Firestore write.
        if (init?.buildingGroupUID) {
            this.buildingGroupUID = init.buildingGroupUID;
        }
        if (init?.allocations && init.allocations.length > 0) {
            this.allocations = init.allocations.map(a => ({
                propertyUID: a.propertyUID,
                share: a.share,
                purchasePrice: a.purchasePrice
            }));
        }

        // Lifecycle amendments + status. Normalise event dates to Timestamp so a
        // Firestore-hydrated loan and a freshly-built one behave identically.
        this.loanEvents = (init?.loanEvents ?? []).map(e => Loan.normalizeEvent(e));
        this.status = init?.status ?? LoanStatus.Active;
        if (init?.refinancedFromLoanUID) {
            this.refinancedFromLoanUID = init.refinancedFromLoanUID;
        }

        this.loanTable = init?.loanTable ?? [];
        this.calculateLoanTable();
    }

    setCreditCost(openingFees: number, recurringFees: number, recurringPeriod: number, mortgageFees: number): void {
        this.creditCost = { openingFees, recurringFees, recurringFeePeriod: recurringPeriod, mortageFee: mortgageFees };
    }

    setInterest(rate: number, interestPaymentPeriod: PaymentPeriod, variable = false): void {
        this.interest = { rate, variable, interestPaymentPeriod };
    }

    setCapital(amount: number, repayment: boolean, fixedInstallment: boolean, capitalPaymentPeriod: number): void {
        this.capital = { nominalAmount: amount, repayment, fixedInstallment, capitalPaymentPeriod };
    }

    // Get the appropriate loan type string for the calculator
    private getCalculatorLoanType(): string {
        if (this.loanType) {
            return this.loanType.toLowerCase();
        }
        
        // Maintain backward compatibility with existing data
        if (!this.capital.repayment) {
            return LoanType.InterestOnly;
        }
        
        return LoanType.Fixed;
    }

    getCapitalRemainingDue(atDate: Date): number {
        if (!this.loanTable || this.loanTable.length === 0) return this.capital.nominalAmount;

        // If date is before loan starts, return full amount
        if (atDate < this.startDate.toDate()) return this.capital.nominalAmount;

        // Find the last payment period that occurs before or on the given date
        const lastPeriodBeforeDate = [...this.loanTable]
            .reverse()
            .find(line => new Date(line.date) <= atDate);
            
        // If we found a period, return its ending balance
        if (lastPeriodBeforeDate) {
            return lastPeriodBeforeDate.endingPrincipal <= 0.000001 ? 0 : lastPeriodBeforeDate.endingPrincipal;
        }
        
        // If date is after loan ends, return 0 (fully paid)
        const loanEndDate = this.getEndDate();
        if (loanEndDate && atDate > loanEndDate.toDate()) {
            return 0;
        }
        
        // Fallback to full amount if we can't determine
        return this.capital.nominalAmount;
    }

    getCurrentPeriodInterestPayment(atDate: Date): number {
        if (!this.loanTable || this.loanTable.length === 0) return 0;

        const relevantLine = this.loanTable.find(line => new Date(line.date) > atDate);
        return relevantLine ? relevantLine.interest : 0;
    }

    getCurrentPeriodCashOut(atDate: Date): number {
        if (!this.loanTable || this.loanTable.length === 0) return 0;

        const relevantLine = this.loanTable.find(line => new Date(line.date) > atDate);
        return relevantLine ? (relevantLine.principalRepayment + relevantLine.interest) : 0;
    }

    getEndDate(): Timestamp | undefined {
        return this.loanTable && this.loanTable.length > 0
            ? Timestamp.fromDate(new Date(this.loanTable[this.loanTable.length - 1].date))
            : undefined;
    }

    getCashFlowForPeriod(startDate: Date, endDate: Date): number {
        return -this.loanTable
            .filter(line => {
                const lineDate = new Date(line.date);
                return lineDate >= startDate && lineDate <= endDate;
            })
            .reduce((total, line) => total + (line.principalRepayment + line.interest + line.fees), 0);
    }

    calculateLoanTable(): void {
        // If the calculator service is not available, use fallback calculation
        if (!Loan._calculator) {
            this.calculateLoanTableFallback();
            return;
        }

        // Loans carrying lifecycle amendments are built by the segmented engine,
        // which reproduces the legacy schedule exactly when there are no events.
        if (this.loanEvents && this.loanEvents.length > 0) {
            this.calculateSegmentedTable();
            return;
        }

        this.loanTable = [];
        let currentDate = this.startDate.toDate();
        let remainingBalance = this.capital.nominalAmount;
        
        // Determine the appropriate loan type
        const loanType = this.getCalculatorLoanType();
        
        // Set up additional parameters for specialized loan calculations
        const additionalParams: { interestOnlyPeriodMonths?: number } = {};
        if (loanType === LoanType.InterestOnly && !this.capital.repayment) {
            // For interest-only loans, the entire term is interest-only
            additionalParams.interestOnlyPeriodMonths = this.numberOfMonths;
        }
        
        // Calculate the fixed monthly payment based on loan type
        const periodsPerYear = this.getPeriodsPerYear(this.interest.interestPaymentPeriod);
        const totalPeriods = Math.ceil(this.numberOfMonths / (12 / periodsPerYear));
        
        // IMPORTANT: Convert interest rate from decimal (0.04) to percentage (4.0) format
        // for the calculator service
        const interestRatePercentage = this.interest.rate * 100;
        
        for (let period = 0; period <= totalPeriods; period++) {
            const entry = new LoanTableEntry();
            entry.period = period;
            entry.date = new Date(currentDate);
            entry.startingPrincipal = remainingBalance;

            if (period === 0) {
                // Initial fees only
                entry.fees = this.creditCost.mortageFee + this.creditCost.openingFees;
                entry.endingPrincipal = remainingBalance;
                entry.totalCashout = entry.fees;
            } else {
                // Use the calculator service to determine payment breakdown
                if (Loan._calculator) {
                    const paymentBreakdown = Loan._calculator.calculatePaymentBreakdown(
                        this.capital.nominalAmount,
                        interestRatePercentage, // Use the converted value (percentage format)
                        totalPeriods, // term expressed in PAYMENT PERIODS (not months)
                        period,
                        remainingBalance,
                        loanType,
                        additionalParams,
                        periodsPerYear // honor Monthly/Quarterly/Yearly frequency
                    );
                    
                    entry.interest = paymentBreakdown.interest;
                    entry.principalRepayment = paymentBreakdown.principal;
                } else {
                    // Fallback calculation if calculator not available (should not happen)
                    const monthlyRate = this.interest.rate / 12;
                    entry.interest = remainingBalance * monthlyRate;
                    
                    if (this.capital.repayment) {
                        // Standard amortizing formula for fixed payment.
                        // We're in the outer `else` branch where
                        // `_calculator` is null — always use the fallback.
                        const fixedPayment = this.calculateFallbackFixedRatePayment();

                        entry.principalRepayment = Math.min(fixedPayment - entry.interest, remainingBalance);
                    } else if (period === totalPeriods) {
                        // For interest-only loans, pay off the principal at the end
                        entry.principalRepayment = remainingBalance;
                    }
                }

                // Add recurring fees if applicable
                if (this.creditCost.recurringFeePeriod > 0 && period % this.creditCost.recurringFeePeriod === 0) {
                    entry.fees = this.creditCost.recurringFees;
                }

                // Update remaining balance
                remainingBalance = Math.max(0, remainingBalance - entry.principalRepayment);
                entry.endingPrincipal = remainingBalance;
                entry.totalCashout = entry.principalRepayment + entry.interest + entry.fees;
            }

            this.loanTable.push(entry);

            // Don't add another period if we've reached the end
            if (period === totalPeriods) break;
            
            currentDate = this.addPeriod(currentDate, this.interest.interestPaymentPeriod);
        }
    }
    
    /**
     * Build the amortization table via the segmented engine (loans WITH events).
     * Reuses the same `calculateFixedRatePayment` math as the legacy path, so the
     * pre-event portion of the schedule is identical to the cent.
     */
    private calculateSegmentedTable(): void {
        const calc = Loan._calculator;
        if (!calc) { this.calculateLoanTableFallback(); return; }

        const entries = buildSegmentedSchedule({
            startDate: this.startDate.toDate(),
            nominalAmount: this.capital.nominalAmount,
            annualRatePct: this.interest.rate * 100,
            numberOfMonths: this.numberOfMonths,
            periodsPerYear: this.getPeriodsPerYear(this.interest.interestPaymentPeriod),
            loanType: this.getCalculatorLoanType() as EngineLoanType,
            openingFees: this.creditCost.openingFees,
            mortageFee: this.creditCost.mortageFee,
            recurringFees: this.creditCost.recurringFees,
            recurringFeePeriod: this.creditCost.recurringFeePeriod,
            events: this.toEngineEvents(),
            fixedRatePayment: (p, r, t, ppy) => calc.calculateFixedRatePayment(p, r, t, ppy)
        });

        this.loanTable = entries.map(e => {
            const entry = new LoanTableEntry();
            entry.period = e.period;
            entry.date = e.date;
            entry.startingPrincipal = e.startingPrincipal;
            entry.interest = e.interest;
            entry.principalRepayment = e.principalRepayment;
            entry.endingPrincipal = e.endingPrincipal;
            entry.fees = e.fees;
            entry.totalCashout = e.totalCashout;
            if (e.event) entry.event = e.event;
            if (e.suspended) entry.suspended = true;
            return entry;
        });
    }

    /** Map the persisted `LoanEvent[]` to the engine's primitive event shape. */
    private toEngineEvents(): EngineEvent[] {
        return this.loanEvents
            .filter(e => !!e.effectiveDate)
            .map(e => {
                const ev: EngineEvent = {
                    type: e.type as EngineEvent['type'],
                    effectiveDate: e.effectiveDate.toDate(),
                    label: e.type
                };
                if (typeof e.amount === 'number') ev.amount = e.amount;
                if (e.repaymentMode) ev.repaymentMode = e.repaymentMode as EngineEvent['repaymentMode'];
                if (e.suspensionEndDate) ev.suspensionEndDate = e.suspensionEndDate.toDate();
                if (e.suspensionScope) ev.suspensionScope = e.suspensionScope as EngineEvent['suspensionScope'];
                if (e.suspensionAfter) ev.suspensionAfter = e.suspensionAfter as EngineEvent['suspensionAfter'];
                if (typeof e.newRate === 'number') ev.newRatePct = e.newRate * 100;
                if (e.rateRecalc) ev.rateRecalc = e.rateRecalc as EngineEvent['rateRecalc'];
                if (typeof e.newNumberOfMonths === 'number') ev.newNumberOfMonths = e.newNumberOfMonths;
                if (typeof e.indemnity === 'number') ev.indemnity = e.indemnity;
                if (typeof e.fees === 'number') ev.fees = e.fees;
                return ev;
            });
    }

    /** Coerce an event's date fields to Timestamp (Firestore hydrate / code create). */
    private static normalizeEvent(e: LoanEvent): LoanEvent {
        const toTs = (v: unknown): Timestamp | undefined => {
            if (!v) return undefined;
            if (v instanceof Timestamp) return v;
            if (v instanceof Date) return Timestamp.fromDate(v);
            const o = v as { seconds?: number; nanoseconds?: number; toDate?: () => Date };
            if (typeof o.toDate === 'function') return Timestamp.fromDate(o.toDate());
            if (typeof o.seconds === 'number') return new Timestamp(o.seconds, o.nanoseconds ?? 0);
            return undefined;
        };
        const out: LoanEvent = { ...e };
        const eff = toTs(e.effectiveDate);
        if (eff) out.effectiveDate = eff;
        const end = toTs(e.suspensionEndDate);
        if (end) out.suspensionEndDate = end;
        return out;
    }

    // Fallback calculation if the calculator service is not available
    private calculateLoanTableFallback(): void {
        this.loanTable = [];
        let currentDate = this.startDate.toDate();
        let remainingBalance = this.capital.nominalAmount;

        // Calculate the fixed monthly payment for an amortizing loan
        let fixedPayment: number;
        const periodsPerYear = this.getPeriodsPerYear(this.interest.interestPaymentPeriod);
        const totalPeriods = Math.ceil(this.numberOfMonths / (12 / periodsPerYear));
        
        // Calculate periodic rate from annual rate (already in decimal format)
        const periodicRate = this.interest.rate / periodsPerYear;

        if (this.capital.repayment) {
            // Standard amortizing loan formula: PMT = P * r * (1+r)^n / ((1+r)^n - 1)
            fixedPayment = remainingBalance * periodicRate * 
                Math.pow(1 + periodicRate, totalPeriods) / 
                (Math.pow(1 + periodicRate, totalPeriods) - 1);
        } else {
            // Interest-only loan: payment is just the interest
            fixedPayment = remainingBalance * periodicRate;
        }

        for (let period = 0; period <= totalPeriods; period++) {
            const entry = new LoanTableEntry();
            entry.period = period;
            entry.date = new Date(currentDate);
            entry.startingPrincipal = remainingBalance;

            if (period === 0) {
                // Initial fees only
                entry.fees = this.creditCost.mortageFee + this.creditCost.openingFees;
                entry.endingPrincipal = remainingBalance;
                entry.totalCashout = entry.fees;
            } else {
                // Calculate interest based on remaining balance
                entry.interest = remainingBalance * periodicRate;

                // For amortizing loans, principal is the difference between fixed payment and interest
                if (this.capital.repayment) {
                    entry.principalRepayment = Math.min(fixedPayment - entry.interest, remainingBalance);
                } else if (period === totalPeriods) {
                    // For interest-only loans, pay off the principal at the end
                    entry.principalRepayment = remainingBalance;
                }

                // Add recurring fees if applicable
                if (this.creditCost.recurringFeePeriod > 0 && period % this.creditCost.recurringFeePeriod === 0) {
                    entry.fees = this.creditCost.recurringFees;
                }

                // Update remaining balance
                remainingBalance = Math.max(0, remainingBalance - entry.principalRepayment);
                entry.endingPrincipal = remainingBalance;
                entry.totalCashout = entry.principalRepayment + entry.interest + entry.fees;
            }

            this.loanTable.push(entry);

            // Don't add another period if we've reached the end
            if (period === totalPeriods) break;
            
            currentDate = this.addPeriod(currentDate, this.interest.interestPaymentPeriod);
        }
    }
    
    // Fallback calculation for fixed rate payment
    private calculateFallbackFixedRatePayment(): number {
        const periodsPerYear = this.getPeriodsPerYear(this.interest.interestPaymentPeriod);
        const totalPeriods = Math.ceil(this.numberOfMonths / (12 / periodsPerYear));
        
        // Calculate periodic rate from annual rate (already in decimal format)
        const periodicRate = this.interest.rate / periodsPerYear;
        
        if (periodicRate === 0) return this.capital.nominalAmount / totalPeriods;
        
        return this.capital.nominalAmount * periodicRate * 
            Math.pow(1 + periodicRate, totalPeriods) / 
            (Math.pow(1 + periodicRate, totalPeriods) - 1);
    }

    private getPeriodsPerYear(period: PaymentPeriod): number {
        switch (period) {
            case PaymentPeriod.Monthly:
                return 12;
            case PaymentPeriod.Quarterly:
                return 4;
            case PaymentPeriod.Yearly:
                return 1;
            default:
                throw new Error('Invalid payment period');
        }
    }

    private addPeriod(date: Date, period: PaymentPeriod): Date {
        const newDate = new Date(date);
        switch (period) {
            case PaymentPeriod.Monthly:
                newDate.setMonth(newDate.getMonth() + 1);
                break;
            case PaymentPeriod.Quarterly:
                newDate.setMonth(newDate.getMonth() + 3);
                break;
            case PaymentPeriod.Yearly:
                newDate.setFullYear(newDate.getFullYear() + 1);
                break;
            default:
                throw new Error('Invalid payment period');
        }
        return newDate;
    }

    getTotalInterestPaid(): number {
        return this.loanTable
            .filter(entry => entry.period !== 0) // Skip the initial entry (period 0)
            .reduce((total, entry) => total + entry.interest, 0);
    }

    getTotalFeesPaid(): number {
        return this.loanTable.reduce((total, entry) => total + entry.fees, 0);
    }
}

// LoanLine represents a line in the amortization table used for display purposes
export class LoanLine {
    period!: number | string;
    paymentDate!: Date;
    startBalance!: number;
    payment!: number;
    interestPayment!: number;
    principalPayment!: number;
    fees!: number;
    endBalance!: number;
}

// LoanTableEntry represents a row in the loan amortization table
export class LoanTableEntry {
    period: number | string = 0;
    date: Date = new Date();
    startingPrincipal = 0;
    interest = 0;
    principalRepayment = 0;
    endingPrincipal = 0;
    fees = 0;
    totalCashout = 0;
    /** Runtime-only segment-boundary marker (not persisted). */
    event?: { type: string; label: string };
    /** Runtime-only flag for rows inside a capital-suspension window (not persisted). */
    suspended?: boolean;
}

/**
 * Contract for the loan calculator that services inject into the
 * static `Loan.calculator` slot. The shape mirrors the public surface
 * of `LoanCalculatorService` — declared here so the domain file stays
 * independent of the feature-layer service class.
 */
export interface LoanCalculatorContract {
  calculatePaymentBreakdown(
    principal: number,
    interestRate: number,
    termMonths: number,
    currentMonth: number,
    remainingBalance: number,
    loanType?: string,
    additionalParams?: {
      interestOnlyPeriodMonths?: number;
      bulletAmount?: number;
      fixedPeriodMonths?: number;
      revisionPeriodMonths?: number;
    },
    periodsPerYear?: number
  ): { principal: number; interest: number };

  calculateFixedRatePayment(
    principal: number,
    interestRate: number,
    termMonths: number,
    periodsPerYear?: number
  ): number;
}